import { tableCellText, type TableCellValue } from "../components/ui/TableCell/TableCell";
import { isSpeechSupported } from "./speak";

export interface ReadAloudSegment {
    text: string;
    lang: string;
    blockIndex: number;
}

export interface ReadAloudBlock {
    text?: string;
    heading?: string;
    callout?: string;
    list?: string[];
    parts?: Array<string | { label: string }>;
    table?: { caption: string; headers: string[]; rows: TableCellValue[][] };
    [key: string]: unknown;
}

export interface ReadAloudProgress {
    segmentIndex: number;
    blockIndex: number;
    /** 1-based position of the current block among the spoken blocks. */
    blockPosition: number;
    blockTotal: number;
}

// Chrome silently stalls utterances that run longer than roughly 15 seconds.
export const MAX_SEGMENT_LENGTH = 180;

const JAPANESE_CHAR = /[぀-ヿㇰ-ㇿ㐀-䶿一-鿿豈-﫿ｦ-ﾟ　-〿]/;
const SPEAKABLE = /[\p{L}\p{N}]/u;

function splitRunsByScript(text: string): Array<{ text: string; japanese: boolean }> {
    const runs: Array<{ text: string; japanese: boolean }> = [];
    for (const char of text) {
        const isJapanese = JAPANESE_CHAR.test(char);
        const neutral = !isJapanese && !/[\p{L}\p{N}]/u.test(char);
        const last = runs[runs.length - 1];
        // Spaces and ASCII punctuation stay with whichever run they follow.
        if (last && (neutral || last.japanese === isJapanese)) {
            last.text += char;
        } else {
            runs.push({ text: char, japanese: isJapanese });
        }
    }
    return runs;
}

const PARTICLE = /[はがをにでとのもへ]/;

function packPieces(pieces: string[], limit: number): string[] {
    const chunks: string[] = [];
    let current = "";
    for (const piece of pieces) {
        if (current && current.length + piece.length > limit) {
            chunks.push(current.trim());
            current = "";
        }
        current += piece;
    }
    if (current.trim()) chunks.push(current.trim());
    return chunks;
}

// Splits an oversized piece with no punctuation. Japanese is cut after a
// particle (never mid-word, and left whole when there is none); other
// languages are cut at a space, then at the limit as a last resort.
function cutOversized(piece: string, japanese: boolean): string[] {
    const chunks: string[] = [];
    let rest = piece;
    while (rest.length > MAX_SEGMENT_LENGTH) {
        let at = -1;
        if (japanese) {
            for (let i = MAX_SEGMENT_LENGTH - 1; i > 0; i--) {
                if (PARTICLE.test(rest[i])) {
                    at = i + 1;
                    break;
                }
            }
            if (at < 0) break;
        } else {
            const space = rest.lastIndexOf(" ", MAX_SEGMENT_LENGTH);
            at = space > 0 ? space : MAX_SEGMENT_LENGTH;
        }
        chunks.push(rest.slice(0, at).trim());
        rest = rest.slice(at);
    }
    if (rest.trim()) chunks.push(rest.trim());
    return chunks;
}

function splitLongText(text: string, japanese: boolean): string[] {
    if (text.length <= MAX_SEGMENT_LENGTH) return [text];
    const pieces = (text.match(/[^.!?。！？、]+[.!?。！？、]*\s*/g) ?? [text]).flatMap((piece) =>
        piece.length > MAX_SEGMENT_LENGTH ? cutOversized(piece, japanese) : [piece]
    );
    return packPieces(pieces, MAX_SEGMENT_LENGTH).filter(Boolean);
}

function blockTexts(block: ReadAloudBlock): string[] {
    if (block.text) return [block.text];
    if (block.heading) return [block.heading];
    if (block.callout) return [block.callout];
    if (block.list) return block.list;
    if (block.parts) return [block.parts.map((part) => (typeof part === "string" ? part : part.label)).join("")];
    if (block.table) {
        const { caption, headers, rows } = block.table;
        const rowTexts = rows.map((row) =>
            row
                .map((cell, index) => {
                    const value = tableCellText(cell);
                    return headers[index] ? `${headers[index]}: ${value}` : value;
                })
                .join("; ")
        );
        return [caption, ...rowTexts];
    }
    return [];
}

export function buildReadAloudSegments(
    content: ReadAloudBlock[],
    { lang = "en-GB" }: { lang?: string } = {}
): ReadAloudSegment[] {
    const segments: ReadAloudSegment[] = [];
    content.forEach((block, blockIndex) => {
        for (const text of blockTexts(block)) {
            for (const run of splitRunsByScript(text)) {
                if (!SPEAKABLE.test(run.text)) continue;
                for (const piece of splitLongText(run.text.trim(), run.japanese)) {
                    segments.push({ text: piece, lang: run.japanese ? "ja-JP" : lang, blockIndex });
                }
            }
        }
    });
    return segments;
}

export function readAloudLang(postLang?: string): string {
    return postLang === "pt-PT" ? "pt-PT" : "en-GB";
}

export interface ReadAloudPlayer {
    play: () => void;
    pause: () => void;
    resume: () => void;
    stop: () => void;
}

function isLocalOnlyNeeded(): boolean {
    return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function pickVoice(lang: string): SpeechSynthesisVoice | undefined {
    const all = window.speechSynthesis.getVoices?.() ?? [];
    // Offline, only on-device voices can work (mirrors speak.ts).
    const voices = isLocalOnlyNeeded() ? all.filter((voice) => voice.localService) : all;
    const normalized = lang.toLowerCase();
    const prefix = normalized.split("-")[0];
    return (
        voices.find((voice) => voice.lang.toLowerCase().replace("_", "-") === normalized) ??
        voices.find((voice) => voice.lang.toLowerCase().split(/[-_]/)[0] === prefix)
    );
}

export function hasJapaneseVoice(): boolean {
    if (!isSpeechSupported()) return false;
    return Boolean(pickVoice("ja-JP"));
}

const KEEP_ALIVE_MS = 10_000;
const PAUSE_CHECK_MS = 50;

function isChromeLike(): boolean {
    return typeof navigator !== "undefined" && /Chrome|Chromium|CriOS|Edg\//.test(navigator.userAgent);
}

export function createReadAloudPlayer(
    segments: ReadAloudSegment[],
    {
        onProgress,
        onEnd,
        onError,
        onPauseRejected,
        rate = 1,
    }: {
        onProgress?: (progress: ReadAloudProgress) => void;
        onEnd?: () => void;
        onError?: () => void;
        /** The browser ignored pause(); the UI should show "playing" again. */
        onPauseRejected?: () => void;
        /** Speech rate for every utterance (1 = normal). */
        rate?: number;
    } = {}
): ReadAloudPlayer {
    const blockOrder = Array.from(new Set(segments.map((segment) => segment.blockIndex)));
    // Bumped on every play/stop so late events from a cancelled utterance are ignored.
    let run = 0;
    let active = false;
    let paused = false;
    // Held so the engine cannot garbage-collect the utterance before onend (Chrome).
    const held: { utterance: SpeechSynthesisUtterance | null } = { utterance: null };
    let keepAlive: ReturnType<typeof setInterval> | undefined;

    const stopKeepAlive = () => {
        if (keepAlive !== undefined) clearInterval(keepAlive);
        keepAlive = undefined;
    };

    const startKeepAlive = () => {
        stopKeepAlive();
        if (!isChromeLike()) return;
        keepAlive = setInterval(() => {
            const synth = window.speechSynthesis;
            if (active && !paused && synth.speaking) {
                synth.pause();
                synth.resume();
            }
        }, KEEP_ALIVE_MS);
    };

    const cancelSpeech = () => {
        window.speechSynthesis.cancel();
        // A cancel issued while paused can leave the engine paused for the next utterance.
        window.speechSynthesis.resume();
    };

    const finish = (callback?: () => void) => {
        active = false;
        paused = false;
        run++;
        held.utterance = null;
        stopKeepAlive();
        callback?.();
    };

    const speakAt = (index: number, myRun: number) => {
        if (myRun !== run) return;
        if (index >= segments.length) {
            finish(onEnd);
            return;
        }
        const segment = segments[index];
        try {
            const utterance = new SpeechSynthesisUtterance(segment.text);
            utterance.lang = segment.lang;
            utterance.rate = rate;
            const voice = pickVoice(segment.lang);
            if (voice) utterance.voice = voice;
            utterance.onend = () => speakAt(index + 1, myRun);
            utterance.onerror = (event) => {
                if (myRun !== run) return;
                const reason = (event as SpeechSynthesisErrorEvent).error;
                cancelSpeech();
                // Cancelled/interrupted by something else: reset quietly instead of hanging.
                finish(reason === "canceled" || reason === "interrupted" ? onEnd : onError);
            };
            held.utterance = utterance;
            onProgress?.({
                segmentIndex: index,
                blockIndex: segment.blockIndex,
                blockPosition: blockOrder.indexOf(segment.blockIndex) + 1,
                blockTotal: blockOrder.length,
            });
            window.speechSynthesis.speak(utterance);
        } catch {
            finish(onError);
        }
    };

    return {
        play() {
            if (!isSpeechSupported() || segments.length === 0) {
                onError?.();
                return;
            }
            run++;
            paused = false;
            cancelSpeech();
            active = true;
            startKeepAlive();
            speakAt(0, run);
        },
        pause() {
            if (!active || paused || !isSpeechSupported()) return;
            paused = true;
            window.speechSynthesis.pause();
            const myRun = run;
            setTimeout(() => {
                if (myRun === run && active && paused && !window.speechSynthesis.paused) {
                    paused = false;
                    onPauseRejected?.();
                }
            }, PAUSE_CHECK_MS);
        },
        resume() {
            if (!active || !isSpeechSupported()) return;
            paused = false;
            window.speechSynthesis.resume();
        },
        stop() {
            const wasActive = active;
            active = false;
            paused = false;
            run++;
            held.utterance = null;
            stopKeepAlive();
            if (wasActive && isSpeechSupported()) cancelSpeech();
        },
    };
}
